import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { useMediaQuery, useTheme } from "@mui/material";

// Plain MUI table
import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";

// Custom hooks
import useMemberData from "layouts/tables/data/memberReportTabledata";

function Member() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchMemberData,
    handleExport,
  } = useMemberData();

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "phone"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchMemberData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchMemberData(startDate, endDate);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Members...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <PageHeader
        title="Member Report"
        subtitle={
          <>
            {formatDisplayDate(dateRange.startDate)}
            {!isSameDay(dateRange.startDate, dateRange.endDate) &&
              ` to ${formatDisplayDate(dateRange.endDate)}`}
          </>
        }
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  maxDate={dateRange.endDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
                <DatePicker
                  label="To Date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  minDate={dateRange.startDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
              </LocalizationProvider>
            )}

            <Button variant="outlined" onClick={handleTodayClick} sx={{ minWidth: "100px" }}>
              Today
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("csv")}
              startIcon={<Icon>download</Icon>}
            >
              CSV
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("pdf")}
              startIcon={<Icon>picture_as_pdf</Icon>}
            >
              PDF
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("print")}
              startIcon={<Icon>print</Icon>}
            >
              Print
            </Button>
          </>
        }
      />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box>
              <DataTable
                table={{
                  columns,
                  rows: rows.map((row) => ({
                    ...row,
                    // Ensure all data is included even if columns are hidden
                    originalData: row.originalData || {
                      id: row.id,
                      memberName: row.memberName,
                      phone: row.phone,
                      email: row.email,
                      department: row.department,
                      ministry: row.ministry,
                    },
                  })),
                }}
                isSorted={false}
                entriesPerPage={true}
                showTotalEntries={true}
                noEndBorder
                canSearch={true}
                searchLabel="Search Members..."
                printFullTable // Ensure all columns print regardless of screen size
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Member;
